import React from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { usePresentation } from "@/lib/presentation";
import AppLayout from "@/components/AppLayout";
import ProfileSection from "@/components/security/ProfileSection";
import SecuritySection from "@/components/security/SecuritySection";
import PrivacySection from "@/components/security/PrivacySection";
import { Button } from "@/components/ui/button";
import { Languages, LogOut, Play, Square, Sparkles } from "lucide-react";

export default function SettingsPage() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const { presenting, setPresenting } = usePresentation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <AppLayout>
      <header className="mb-10 ag-fade-in">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">{t.nav.settings}</div>
        <h1 className="font-serif text-4xl md:text-5xl text-ag-ink mb-2">{t.settings.title}</h1>
        <p className="text-slate-600 max-w-xl">
          {lang === "de"
            ? "Ihr Konto. Ihre Daten. In Ruhe verwaltet."
            : "Your account. Your data. Managed calmly."}
        </p>
      </header>

      {/* Profil */}
      <div className="ag-fade-in" style={{ animationDelay: "60ms" }}>
        <ProfileSection />
      </div>

      {/* Sicherheit */}
      <div className="mt-6 ag-fade-in" style={{ animationDelay: "120ms" }}>
        <SecuritySection />
      </div>

      {/* Datenschutz */}
      <div className="mt-6 ag-fade-in" style={{ animationDelay: "180ms" }}>
        <PrivacySection />
      </div>

      {/* Allgemein – Sprache + Präsentation */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6 ag-fade-in" style={{ animationDelay: "240ms" }}>
        <section className="ag-card p-7">
          <div className="flex items-center gap-2 mb-4">
            <Languages className="h-4 w-4 text-slate-500" />
            <h2 className="font-serif text-2xl text-ag-ink">{t.settings.language}</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="settings-lang-de"
              onClick={() => setLang("de")}
              className={`p-4 rounded-xl border text-left transition-all ${lang === "de" ? "border-ag-ink bg-ag-ink text-white" : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="font-serif text-lg">{t.settings.german}</div>
              <div className="text-xs opacity-70">Standardsprache</div>
            </button>
            <button
              data-testid="settings-lang-en"
              onClick={() => setLang("en")}
              className={`p-4 rounded-xl border text-left transition-all ${lang === "en" ? "border-ag-ink bg-ag-ink text-white" : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="font-serif text-lg">{t.settings.english}</div>
              <div className="text-xs opacity-70">For international guests</div>
            </button>
          </div>
        </section>

        <section className="ag-card p-7 border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-white">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-amber-600" />
            <h2 className="font-serif text-2xl text-ag-ink">{t.settings.presentation}</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">{t.settings.presentationSub}</p>
          {!presenting ? (
            <Button
              onClick={() => setPresenting(true)}
              data-testid="settings-presentation-start"
              className="bg-amber-600 hover:bg-amber-700 text-white h-11 rounded-xl"
            >
              <Play className="h-4 w-4 mr-2" /> {t.settings.startPresentation}
            </Button>
          ) : (
            <Button
              onClick={() => setPresenting(false)}
              variant="outline"
              data-testid="settings-presentation-stop"
              className="h-11 rounded-xl border-amber-300"
            >
              <Square className="h-4 w-4 mr-2" /> {t.settings.stopPresentation}
            </Button>
          )}
        </section>
      </div>

      {/* Account-Footer */}
      <section className="ag-card p-7 mt-6 ag-fade-in" style={{ animationDelay: "300ms" }}>
        <h2 className="font-serif text-2xl text-ag-ink mb-4">{t.settings.account}</h2>
        <div className="flex flex-wrap items-center gap-4">
          {user?.is_demo && (
            <Button
              variant="outline" onClick={handleLogout}
              data-testid="settings-exit-demo"
              className="h-10 rounded-xl border-amber-300 text-amber-800"
            >
              {t.settings.exitDemo} →
            </Button>
          )}
          <Button
            variant="outline" onClick={handleLogout}
            data-testid="settings-logout"
            className="h-10 rounded-xl border-slate-200"
          >
            <LogOut className="h-4 w-4 mr-2" /> {t.auth.logout}
          </Button>
        </div>
      </section>
    </AppLayout>
  );
}
